/**
 * Tour → lugares visitados (fuente para las guías de Maya).
 *
 * Fuente principal: itinerario estructurado del producto (títulos y
 * descripciones de cada parada). Después highlights. El título solo se usa
 * como respaldo. Las frases de logística (recogida, punto de encuentro,
 * traslado, regreso) se descartan para que la ciudad de salida no se tome
 * como lugar visitado. Si no se reconoce ningún lugar, no se inventa nada.
 */

export interface TourPlace {
  slug: string;
  name: string;
}

export type TourPlacesSource = "itinerary" | "highlights" | "title" | "none";

interface PlaceDef extends TourPlace {
  /** Alias normalizados (sin acentos, minúsculas). */
  aliases: string[];
}

const P = (slug: string, name: string, ...aliases: string[]): PlaceDef => ({
  slug,
  name,
  aliases: [norm(name), ...aliases.map(norm)],
});

function norm(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

// Orden: los nombres más específicos primero (p. ej. "Cenote Ik Kil" antes que "Chichén Itzá").
const GAZETTEER: PlaceDef[] = [
  // Holbox y alrededores
  P("holbox", "Holbox", "isla holbox"),
  P("punta-coco", "Punta Coco"),
  P("punta-mosquito", "Punta Mosquito"),
  P("isla-pasion", "Isla Pasión", "isla de la pasion"),
  P("cabo-catoche", "Cabo Catoche"),
  // Yucatán
  P("chichen-itza", "Chichén Itzá", "chichen itza", "chichenitza", "chichen"),
  P("cenote-ik-kil", "Cenote Ik Kil", "ik kil", "ikkil"),
  P("cenote-hubiku", "Cenote Hubiku", "hubiku"),
  P("cenote-saamal", "Cenote Saamal", "saamal"),
  P("cenote-suytun", "Cenote Suytun", "suytun"),
  P("cenote-zaci", "Cenote Zací", "zaci"),
  P("cenote-xcajum", "Cenote Xcajum", "xcajum"),
  P("valladolid", "Valladolid"),
  P("ek-balam", "Ek Balam", "ekbalam"),
  P("izamal", "Izamal"),
  P("merida", "Mérida"),
  P("uxmal", "Uxmal"),
  P("celestun", "Celestún"),
  P("las-coloradas", "Las Coloradas", "coloradas", "lagunas rosadas", "lagos rosados"),
  P("rio-lagartos", "Río Lagartos", "rio lagartos"),
  P("el-cuyo", "El Cuyo"),
  P("sisal", "Sisal"),
  // Riviera Maya
  P("tulum", "Tulum", "zona arqueologica de tulum"),
  P("akumal", "Akumal"),
  P("coba", "Cobá"),
  P("gran-cenote", "Gran Cenote"),
  P("cenote-dos-ojos", "Cenote Dos Ojos", "dos ojos"),
  P("xel-ha", "Xel-Há", "xel ha", "xelha"),
  P("xcaret", "Xcaret"),
  P("puerto-morelos", "Puerto Morelos"),
  P("playa-del-carmen", "Playa del Carmen", "quinta avenida"),
  P("cozumel", "Cozumel"),
  P("isla-mujeres", "Isla Mujeres"),
  P("isla-contoy", "Isla Contoy", "contoy"),
  P("ruta-de-los-cenotes", "Ruta de los Cenotes"),
  P("sian-kaan", "Sian Ka'an", "sian kaan", "muyil"),
  // Bacalar y sur
  P("bacalar", "Bacalar", "laguna de los siete colores", "laguna de bacalar", "laguna de siete colores"),
  P("canal-de-los-piratas", "Canal de los Piratas", "canal de piratas"),
  P("cenote-negro", "Cenote Negro", "cenote de la bruja"),
  P("isla-de-los-pajaros", "Isla de los Pájaros", "isla de pajaros"),
  P("cenote-esmeralda", "Cenote Esmeralda"),
  P("estromatolitos", "Estromatolitos"),
  P("cenote-azul", "Cenote Azul"),
  P("los-rapidos", "Los Rápidos de Bacalar", "rapidos de bacalar", "los rapidos"),
  P("mahahual", "Mahahual"),
  P("chetumal", "Chetumal"),
  P("calakmul", "Calakmul"),
  P("palenque", "Palenque"),
  // Ciudades de salida frecuentes (solo cuentan si el itinerario las visita)
  P("cancun", "Cancún"),
];

const LOGISTICS = [
  "recogida", "recoger", "pick up", "pickup", "punto de encuentro", "puntos de origen",
  "hotel", "traslado", "regreso", "regresar", "salida", "transporte", "check in",
  "drop off", "pick-up", "return", "meeting point", "transfer",
];

function stripHtml(s: string | null | undefined): string {
  return (s ?? "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&[a-z]+;/gi, " ");
}

/** Divide en frases y descarta las que solo describen logística. */
function meaningfulText(raw: string, exclude: string[]): string {
  return stripHtml(raw)
    .split(/[.\n!?;]+/)
    .map((s) => norm(s))
    .filter((s) => s && !LOGISTICS.some((w) => s.includes(w)))
    .map((s) => exclude.reduce((acc, e) => (e ? acc.split(e).join(" ") : acc), s))
    .join(" | ");
}

function matchPlaces(text: string): TourPlace[] {
  const padded = ` ${text} `;
  const found: { place: PlaceDef; at: number }[] = [];
  for (const place of GAZETTEER) {
    let at = -1;
    for (const a of place.aliases) {
      const i = padded.indexOf(` ${a} `);
      if (i >= 0 && (at < 0 || i < at)) at = i;
    }
    if (at >= 0) found.push({ place, at });
  }
  return found
    .sort((a, b) => a.at - b.at)
    .map(({ place }) => ({ slug: place.slug, name: place.name }));
}

export function detectTourPlaces(input: {
  title: string;
  itinerary?: { title: string; body?: string | null }[] | null;
  highlights?: string[] | null;
  meetingPoints?: string[] | null;
}): { places: TourPlace[]; source: TourPlacesSource } {
  const exclude = (input.meetingPoints ?? []).map(norm).filter((s) => s.length > 3);

  const itin = (input.itinerary ?? [])
    .map((s) => `${s.title ?? ""}. ${s.body ?? ""}`)
    .join(". ");
  const fromItin = itin ? matchPlaces(meaningfulText(itin, exclude)) : [];
  if (fromItin.length) return { places: fromItin.slice(0, 8), source: "itinerary" };

  const fromHigh = matchPlaces(meaningfulText((input.highlights ?? []).join(". "), exclude));
  if (fromHigh.length) return { places: fromHigh.slice(0, 8), source: "highlights" };

  const fromTitle = matchPlaces(norm(input.title));
  if (fromTitle.length) return { places: fromTitle.slice(0, 8), source: "title" };

  return { places: [], source: "none" };
}
